import { AppNS } from 'app';
import { addDocument } from 'app/api_v2/files';
import MultiFileUploader2, { FileInfo } from 'app/ui/components/multi-file-uploader2/MultiFileUploader2';
import ButtonLoader from 'app/ui_v2/components/button-loader/ButtonLoader';
import BasicSelect from 'app/ui_v2/form-fields/basic-fields/select/BasicSelect';
import BasicDatePicker from 'app/ui_v2/form-fields/basic-fields/single-datepicker/BasicDatepicker';
import BasicSwitchInput from 'app/ui_v2/form-fields/basic-fields/switch/BasicSwitch';
import BasicTextarea from 'app/ui_v2/form-fields/basic-fields/textarea/BasicTextarea';
import FormWrapper from 'app/ui_v2/form-fields/form-wrapper/FormWrapper';
import { checkErrors, getErrorMessage, mergeData } from 'app/ui_v2/form-utils';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Card, CardContent, Divider, FormControlLabel, FormHelperText, Grid, InputLabel, Radio, RadioGroup, Typography, useTheme } from '@mui/material';

import AdminsSelect from './components/AdminsSelect';
import DisciplinesSelect from './components/DisciplinesSelect';
import ExternalData from './components/ExternalData';
import UsersAutocomplete from './components/UsersAutocomplete';
import { SUBJECT_TYPES, TICKET_TYPES } from './helpers';
import { defaultValues, FormValues, getValidationSchema } from './validation-schema';

export interface NewTicketFormProps {
  onFormSubmit: (values: FormValues) => void;
  onFormCancel?: () => void;
  defaults?: Partial<AppNS.Ticket>;
  meta?: AppNS.Params;
  isLoading?: boolean;
}

export default function NewTicketForm(props: NewTicketFormProps) {
  const { onFormSubmit, defaults, isLoading = false, onFormCancel, meta } = props;

  const theme = useTheme();

  const docsFieldName = 'documentIds';

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: mergeData(defaultValues, defaults || {}),
    resolver: yupResolver(getValidationSchema()),
  });

  const [isReset, setIsReset] = useState<number>(0);

  const onSubmit = (data: FormValues) => {
    onFormSubmit(data);
  };

  const handleChange = () => {};
  const handleCancel = () => {
    onFormCancel && onFormCancel();
  };

  const onFileAdd = (file: FileInfo) => {
    return addDocument({
      type: 'ticket',
      file: file.url ?? '',
      objectId: null,
      objectType: 'v2/ticket',
    });
  };

  const isImmediateDeactivation = watch('isImmediateDeactivation');
  const subject = watch('subject');
  const subjectType = watch('subjectType');
  const inspectorId = watch('inspectorId');
  const companyId = watch('companyId');

  const ticketTypesCollection = TICKET_TYPES.filter((item) => item.isAutomatic === false);

  useEffect(() => {
    if (subjectType && !meta?.isExternal) {
      setValue('subject', null);
      setValue('inspectorProfileId', '');
      setValue('inspectorId', '');
      setValue('companyId', '');
      setIsReset((prev) => prev + 1);
    }
  }, [subjectType, meta?.isExternal]);

  useEffect(() => {
    if (subject && !meta?.isExternal) {
      setValue('disciplineForDeactivationId', '');
      // and reset disciplines collection

      if (subjectType === 'inspector') {
        setValue('inspectorProfileId', subject);
        setValue('companyId', '');
      }
      if (subjectType === 'agency') {
        setValue('companyId', subject);
        setValue('inspectorProfileId', '');
      }
    }
  }, [subject, meta?.isExternal]);

  return (
    <FormWrapper<FormValues> onChangeCallback={handleChange} onSubmitCallback={onSubmit} handleSubmit={handleSubmit}>
      <>
        <Typography component="h3" variant="h5" className="primary" sx={{ mb: 3, fontWeight: 700 }}>
          Subject & Reason Details
        </Typography>

        <Grid container spacing={4}>
          {!meta?.isExternal && (
            <>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <BasicSelect
                  collection={SUBJECT_TYPES}
                  label="Subject Type"
                  inputName={'subjectType'}
                  control={control}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <UsersAutocomplete
                  control={control}
                  disabled={!subjectType}
                  inputName={'subject'}
                  subjectType={subjectType}
                  onValueChange={(value: AppNS.Params) => {
                    // additional handler for fetch userId when subjectType is inspector and we search in inspectors profiles
                    // this is for disciplines search
                    if (subjectType === 'inspector') {
                      setValue('inspectorId', value.userId);
                    } else {
                      setValue('inspectorId', '');
                    }
                  }}
                  isReset={isReset}
                />
              </Grid>
            </>
          )}
          {meta?.isExternal && (
            <Grid item xs={12}>
              <ExternalData meta={meta} defaults={defaults || {}} />
            </Grid>
          )}
          {/* <Grid item xs={12}>
            <BasicSelect
              collection={ticketTypesCollection}
              label="Ticket Type"
              inputName={'ticketType'}
              control={control}
            />
          </Grid> */}
          <Grid item xs={12}>
            <BasicTextarea
              control={control}
              inputName="description"
              label={'Description'}
              rows={4}
              maxLength={0}
              helperText="Add any necessary notes here."
              bgColor={theme.palette.background.default}
            />
          </Grid>
        </Grid>

        <Box mt={4} mb={0.5}>
          <InputLabel shrink htmlFor={docsFieldName}>
            Attachment (optional)
          </InputLabel>
        </Box>
        <Box
          sx={{
            minHeight: '128px',
          }}
        >
          <MultiFileUploader2
            uploaderId={`documents-new-ticket`}
            type="documents"
            isLoading={isLoading}
            collectionLimit={5}
            canRemoveFiles={true}
            fileAddAction={onFileAdd}
            collection={[]}
            onChange={(files: FileInfo[]) => {
              setTimeout(() => {
                setValue(
                  docsFieldName,
                  files.map((item) => item.documentId || item.id || ''),
                  { shouldValidate: true }
                );
              }, 10);
            }}
            onUploadStart={() => {}}
            onUploadEnd={() => {}}
          />
        </Box>
        {checkErrors(docsFieldName, errors) && (
          <FormHelperText className="Mui-error">{getErrorMessage(docsFieldName, errors)}</FormHelperText>
        )}

        <Divider
          sx={{
            my: 3,
          }}
        />

        <Typography component="h3" variant="h5" className="primary" sx={{ mb: 3, fontWeight: 700 }}>
          Completion & Deactivation Details
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <AdminsSelect control={control} />
          </Grid>
          <Grid item xs={12}>
            <BasicDatePicker
              label={'Deadline of Completion'}
              inputName={'deadline'}
              control={control}
              disablePast={true}
              isWithCancel={true}
            />
          </Grid>
        </Grid>

        <Box component="p" sx={{ fontSize: '1.6rem' }}>
          The client's profile will be automatically deactivated if they don't act on the ticket by the deadline. Select
          immediate deactivation if you'd like to deactivate the profile or a discipline while the ticket is active.
        </Box>

        <Box
          sx={{
            ml: 1,
            mb: 2,
          }}
        >
          <BasicSwitchInput label="Immediate deactivation" inputName="isImmediateDeactivation" control={control} />
        </Box>

        {isImmediateDeactivation && (
          <Card className="no-shadow card-type-light">
            <CardContent>
              <Typography
                component="h3"
                variant="h5"
                className="primary"
                sx={{ mb: 3, fontWeight: 700, fontSize: '2rem' }}
              >
                Deactivation scope
              </Typography>
              <Controller
                name="deactivationScope"
                control={control}
                render={({ field }) => (
                  <RadioGroup aria-label="Deactivation scope" {...field}>
                    <FormControlLabel
                      value="profile"
                      control={<Radio disabled={!subject} />}
                      label={<Box fontSize="1.6rem">Full Profile</Box>}
                    />
                    <FormControlLabel
                      value="discipline"
                      control={<Radio disabled={!subject} />}
                      label={<Box fontSize="1.6rem">Selected Discipline</Box>}
                    />
                    {field.value === 'discipline' && (
                      <Box mt={2} ml={3.8}>
                        <Grid container columnSpacing={4} spacing={4}>
                          <Grid item xs={12} sm={12} md={6} lg={6}>
                            <DisciplinesSelect
                              subject={inspectorId || companyId}
                              control={control}
                              subjectType={subjectType}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </RadioGroup>
                )}
              />
              {getErrorMessage('deactivationScope', errors) && (
                <FormHelperText className="Mui-error">{getErrorMessage('deactivationScope', errors)}</FormHelperText>
              )}
            </CardContent>
          </Card>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Box mr={2}>
              <Button
                variant="outlined"
                disableElevation
                onClick={() => {
                  handleCancel();
                }}
              >
                Cancel
              </Button>
            </Box>
            <Button type="submit" variant="contained" disabled={isLoading} disableElevation>
              <ButtonLoader isActive={isLoading} />
              Save
            </Button>
          </Box>
        </Grid>
      </>
    </FormWrapper>
  );
}
